import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { captureReferralFromUrl } from './services/referral';
import { recordPath, previousPath } from './services/navigationHistory';
import { pageViewApi } from './services/api';
import { landingVisitorId } from './services/kbLandingRef';
import { ConfigProvider } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import enUS from 'antd/locale/en_US';
import jaJP from 'antd/locale/ja_JP';
import './App.css';
import './portal.css';
import './console.css';
import { antdTheme } from './theme';

// Eager: the portal shell renders around every marketing page, so keeping it in
// the entry bundle is what lets the header and footer appear immediately
// instead of waiting on a second request.
import PortalLayout from './components/portal/PortalLayout';
import RouteFallback from './components/RouteFallback';

// Import authentication context
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Import language context
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';

// Every route below is code-split. A visitor landing on a tool page used to
// download the whole app — dashboard, admin console, charts, QR codes — before
// anything rendered: 4.46 MB of JS (1.47 MB gzip) that on a throttled mobile
// connection left the prerendered SEO body on screen for ~13 s. Splitting per
// route keeps the entry bundle to the shell, and each page pulls only its own
// chunk. `webpackChunkName` groups pages that are always browsed together
// (docs, dashboard, admin, legal) so grouping costs one request, not sixteen.

// Auth / standalone pages
const Login = lazy(() => import(/* webpackChunkName: "auth" */ './pages/Login'));
const Register = lazy(() => import(/* webpackChunkName: "auth" */ './pages/Register'));
const InvitePage = lazy(() => import(/* webpackChunkName: "auth" */ './pages/InvitePage'));
const OAuthConsent = lazy(() => import('./pages/OAuthConsent'));
const Playground = lazy(() => import('./pages/Playground'));
const TrialPage = lazy(() => import('./pages/TrialPage'));
const KbLanding = lazy(() => import('./pages/KbLanding'));
const CheckoutSuccess = lazy(() => import(/* webpackChunkName: "checkout" */ './pages/CheckoutSuccess'));
const CheckoutCancel = lazy(() => import(/* webpackChunkName: "checkout" */ './pages/CheckoutCancel'));

// Dashboard (authenticated)
const DashboardLayout = lazy(() => import(/* webpackChunkName: "dashboard" */ './components/layout/DashboardLayout'));
const Dashboard = lazy(() => import(/* webpackChunkName: "dashboard" */ './pages/Dashboard'));
const KnowledgeBases = lazy(() => import(/* webpackChunkName: "dashboard" */ './pages/KnowledgeBases'));
const KnowledgeBaseDetailLayout = lazy(() => import(/* webpackChunkName: "dashboard" */ './pages/knowledge-base/KnowledgeBaseDetailLayout'));
const KnowledgeBase = lazy(() => import(/* webpackChunkName: "dashboard" */ './pages/KnowledgeBase'));
const Conversations = lazy(() => import(/* webpackChunkName: "dashboard" */ './pages/Conversations'));
const Settings = lazy(() => import(/* webpackChunkName: "dashboard" */ './pages/Settings'));
const Referrals = lazy(() => import(/* webpackChunkName: "dashboard" */ './pages/Referrals'));
const OnboardingPage = lazy(() => import(/* webpackChunkName: "dashboard" */ './pages/OnboardingPage'));
const Followed = lazy(() => import(/* webpackChunkName: "dashboard" */ './pages/Followed'));

// Portal marketing pages
const Home = lazy(() => import('./pages/portal/Home'));
const Features = lazy(() => import('./pages/portal/Features'));
const Pricing = lazy(() => import('./pages/portal/Pricing'));
const Contact = lazy(() => import('./pages/portal/Contact'));
const MCPDocs = lazy(() => import('./pages/portal/MCPDocs'));
const News = lazy(() => import(/* webpackChunkName: "news" */ './pages/portal/News'));
const NewsDetail = lazy(() => import(/* webpackChunkName: "news" */ './pages/portal/NewsDetail'));
const Kumamoto = lazy(() => import(/* webpackChunkName: "kumamoto" */ './pages/portal/Kumamoto'));
const KumamotoComplete = lazy(() => import(/* webpackChunkName: "kumamoto" */ './pages/portal/KumamotoComplete'));
const CompareOverseas = lazy(() => import(/* webpackChunkName: "compare" */ './pages/portal/compare/CompareOverseas'));
const LineAiFeature = lazy(() => import(/* webpackChunkName: "compare" */ './pages/portal/compare/LineAiFeature'));
const CompareEnterprise = lazy(() => import(/* webpackChunkName: "compare" */ './pages/portal/compare/CompareEnterprise'));

// Free tools — the SEO entry points. Each gets its own chunk: a visitor who
// searched for 敬語変換 should not pay for the other twelve tools.
const ToolsIndex = lazy(() => import('./pages/portal/tools/ToolsIndex'));
const FaqGenerator = lazy(() => import('./pages/portal/tools/FaqGenerator'));
const RoiCalculator = lazy(() => import('./pages/portal/tools/RoiCalculator'));
const CrawlPreview = lazy(() => import('./pages/portal/tools/CrawlPreview'));
const KeigoConverter = lazy(() => import('./pages/portal/tools/KeigoConverter'));
const ApologyMail = lazy(() => import('./pages/portal/tools/ApologyMail'));
const InquiryReply = lazy(() => import('./pages/portal/tools/InquiryReply'));
const Proofreader = lazy(() => import('./pages/portal/tools/Proofreader'));
const OgpChecker = lazy(() => import('./pages/portal/tools/OgpChecker'));
const LinkChecker = lazy(() => import('./pages/portal/tools/LinkChecker'));
const SitemapGenerator = lazy(() => import('./pages/portal/tools/SitemapGenerator'));
const PdfSummarizer = lazy(() => import('./pages/portal/tools/PdfSummarizer'));
const PdfToText = lazy(() => import('./pages/portal/tools/PdfToText'));
const PdfToMarkdown = lazy(() => import('./pages/portal/tools/PdfToMarkdown'));

// Admin operations console (platform staff only)
const AdminLayout = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminLayout'));
const AdminOverview = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminOverview'));
const AdminNews = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminNews'));
const AdminUsers = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminUsers'));
const AdminTokens = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminTokens'));
const AdminRevenue = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminRevenue'));
const AdminConversations = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminConversations'));
const AdminStorage = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminStorage'));
const AdminBilling = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminBilling'));
const AdminReferrals = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminReferrals'));
const AdminLLMProviders = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminLLMProviders'));
const AdminTrials = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminTrials'));
const AdminKnowledgeBases = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminKnowledgeBases'));
const AdminKumamoto = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminKumamoto'));
const AdminKumamotoDetail = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminKumamotoDetail'));
const AdminFunnel = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminFunnel'));
const AdminToolFailures = lazy(() => import(/* webpackChunkName: "admin" */ './pages/admin/AdminToolFailures'));

// Developer docs — the sidebar layout ships in the same chunk as the pages it
// links to, so moving between docs pages costs no further requests.
const DocsLayout = lazy(() => import(/* webpackChunkName: "docs" */ './components/portal/DocsLayout'));
const DocsIndex = lazy(() => import(/* webpackChunkName: "docs" */ './pages/portal/docs/DocsIndex'));
const Quickstart = lazy(() => import(/* webpackChunkName: "docs" */ './pages/portal/docs/Quickstart'));
const KnowledgeBasesDoc = lazy(() => import(/* webpackChunkName: "docs" */ './pages/portal/docs/KnowledgeBases'));
const WidgetDoc = lazy(() => import(/* webpackChunkName: "docs" */ './pages/portal/docs/Widget'));
const WidgetAppearanceDoc = lazy(() => import(/* webpackChunkName: "docs" */ './pages/portal/docs/WidgetAppearance'));
const MessagesDoc = lazy(() => import(/* webpackChunkName: "docs" */ './pages/portal/docs/Messages'));
const QueryApiDoc = lazy(() => import(/* webpackChunkName: "docs" */ './pages/portal/docs/QueryApi'));
const McpDoc = lazy(() => import(/* webpackChunkName: "docs" */ './pages/portal/docs/Mcp'));
const BillingDoc = lazy(() => import(/* webpackChunkName: "docs" */ './pages/portal/docs/Billing'));
const SecurityDoc = lazy(() => import(/* webpackChunkName: "docs" */ './pages/portal/docs/Security'));
const FaqDoc = lazy(() => import(/* webpackChunkName: "docs" */ './pages/portal/docs/Faq'));
const ChannelsDoc = lazy(() => import(/* webpackChunkName: "docs" */ './pages/portal/docs/Channels'));
const ConversationsDoc = lazy(() => import(/* webpackChunkName: "docs" */ './pages/portal/docs/Conversations'));
const ContactsDoc = lazy(() => import(/* webpackChunkName: "docs" */ './pages/portal/docs/Contacts'));
const WebhooksDoc = lazy(() => import(/* webpackChunkName: "docs" */ './pages/portal/docs/Webhooks'));
const ToolsWritingDoc = lazy(() => import(/* webpackChunkName: "docs" */ './pages/portal/docs/ToolDocSections').then((m) => ({ default: m.ToolsWritingDoc })));
const ToolsPdfDoc = lazy(() => import(/* webpackChunkName: "docs" */ './pages/portal/docs/ToolDocSections').then((m) => ({ default: m.ToolsPdfDoc })));
const ToolsSiteDoc = lazy(() => import(/* webpackChunkName: "docs" */ './pages/portal/docs/ToolDocSections').then((m) => ({ default: m.ToolsSiteDoc })));
const ToolsChatbotDoc = lazy(() => import(/* webpackChunkName: "docs" */ './pages/portal/docs/ToolDocSections').then((m) => ({ default: m.ToolsChatbotDoc })));

// Legal pages
const PrivacyPolicy = lazy(() => import(/* webpackChunkName: "legal" */ './pages/portal/PrivacyPolicy'));
const TermsOfService = lazy(() => import(/* webpackChunkName: "legal" */ './pages/portal/TermsOfService'));
const Tokushoho = lazy(() => import(/* webpackChunkName: "legal" */ './pages/portal/legal/Tokushoho'));
const CookiePolicy = lazy(() => import(/* webpackChunkName: "legal" */ './pages/portal/legal/CookiePolicy'));
const Dpa = lazy(() => import(/* webpackChunkName: "legal" */ './pages/portal/legal/Dpa'));
const Aup = lazy(() => import(/* webpackChunkName: "legal" */ './pages/portal/legal/Aup'));

// Antd locale mapping
const antdLocales = {
  'zh-CN': zhCN,
  'en-US': enUS,
  'ja-JP': jaJP,
};

// Protected route component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Admin-only route guard. Builds on ProtectedRoute: requires a logged-in user
// whose /api/auth/me reported is_admin. Real enforcement is server-side
// (admin_middleware); this only keeps non-admins out of the /admin UI.
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!user.is_admin) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

// Persist a `?ref=` referral code on any route so it survives navigation/OAuth
// and can be claimed after auth. Renders nothing.
const ReferralCapture: React.FC = () => {
  const location = useLocation();
  React.useEffect(() => {
    captureReferralFromUrl(location.search);
  }, [location.search]);
  return null;
};

// Track the previous in-app path (powers /try's contextual back button).
// Renders nothing.
const PathTracker: React.FC = () => {
  const location = useLocation();
  React.useEffect(() => {
    recordPath(location.pathname);
  }, [location.pathname]);
  return null;
};

// Site-wide page-view beacon. Fires on every route change; the BACKEND
// whitelist decides which paths are stored (marketing surfaces only), so no
// per-page wiring is needed. Must render AFTER PathTracker so previousPath()
// already points at the page navigated from.
const PageViewBeacon: React.FC = () => {
  const location = useLocation();
  const firstView = React.useRef(true);
  const lastSent = React.useRef<{ path: string; at: number }>({ path: '', at: 0 });
  React.useEffect(() => {
    // Dev StrictMode double-invokes effects; a short same-path window keeps
    // one beacon per real view (harmless in production).
    const now = Date.now();
    if (lastSent.current.path === location.pathname && now - lastSent.current.at < 500) {
      return;
    }
    lastSent.current = { path: location.pathname, at: now };
    const src = new URLSearchParams(location.search).get('src') || undefined;
    pageViewApi.record({
      path: location.pathname,
      from_path: previousPath() || undefined,
      src,
      visitor_id: landingVisitorId(),
      // document.referrer only means anything for the entry page; SPA
      // navigations keep the original referrer, which would mislabel later
      // views.
      referrer: firstView.current ? document.referrer || undefined : undefined,
      language: navigator.language,
    });
    firstView.current = false;
  }, [location.pathname, location.search]);
  return null;
};

// Main app component
const AppContent: React.FC = () => {
  const { currentLanguage } = useLanguage();

  return (
    <ConfigProvider locale={antdLocales[currentLanguage as keyof typeof antdLocales] || enUS} theme={antdTheme}>
      <Router>
        <ReferralCapture />
        <PathTracker />
        <PageViewBeacon />
        <div className="App">
          {/* Outer boundary: routes rendered without the portal shell
              (dashboard, admin, /try, auth). Portal pages suspend against
              the boundary inside PortalLayout instead, so their header and
              footer stay on screen while the page chunk loads. */}
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              {/* Portal routes (public) */}
              <Route element={<PortalLayout />}>
                <Route path="/" element={<Home />} />
                <Route path="/features" element={<Features />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/tokushoho" element={<Tokushoho />} />
                <Route path="/cookies" element={<CookiePolicy />} />
                <Route path="/dpa" element={<Dpa />} />
                <Route path="/aup" element={<Aup />} />
                <Route path="/mcp" element={<MCPDocs />} />
                <Route path="/news" element={<News />} />
                <Route path="/news/:idOrSlug" element={<NewsDetail />} />
                <Route path="/kumamoto" element={<Kumamoto />} />
                <Route path="/kumamoto/complete" element={<KumamotoComplete />} />
                <Route path="/tools" element={<ToolsIndex />} />
                <Route path="/tools/faq-generator" element={<FaqGenerator />} />
                <Route path="/tools/chatbot-roi" element={<RoiCalculator />} />
                <Route path="/tools/crawl-preview" element={<CrawlPreview />} />
                <Route path="/tools/keigo-converter" element={<KeigoConverter />} />
                <Route path="/tools/apology-mail" element={<ApologyMail />} />
                <Route path="/tools/inquiry-reply" element={<InquiryReply />} />
                <Route path="/tools/proofreader" element={<Proofreader />} />
                <Route path="/tools/ogp-checker" element={<OgpChecker />} />
                <Route path="/tools/link-checker" element={<LinkChecker />} />
                <Route path="/tools/sitemap-generator" element={<SitemapGenerator />} />
                <Route path="/tools/pdf-summarizer" element={<PdfSummarizer />} />
                <Route path="/tools/pdf-to-text" element={<PdfToText />} />
                <Route path="/tools/pdf-to-markdown" element={<PdfToMarkdown />} />
                <Route path="/compare/overseas-ai-chatbot" element={<CompareOverseas />} />
                <Route path="/line-ai-chatbot" element={<LineAiFeature />} />
                <Route path="/compare/enterprise-chatbot" element={<CompareEnterprise />} />
                <Route path="/docs" element={<DocsLayout />}>
                  <Route index element={<DocsIndex />} />
                  <Route path="quickstart" element={<Quickstart />} />
                  <Route path="knowledge-bases" element={<KnowledgeBasesDoc />} />
                  <Route path="widget" element={<WidgetDoc />} />
                  <Route path="widget-appearance" element={<WidgetAppearanceDoc />} />
                  <Route path="messages" element={<MessagesDoc />} />
                  <Route path="channels" element={<ChannelsDoc />} />
                  <Route path="conversations" element={<ConversationsDoc />} />
                  <Route path="contacts" element={<ContactsDoc />} />
                  <Route path="query-api" element={<QueryApiDoc />} />
                  <Route path="mcp" element={<McpDoc />} />
                  <Route path="webhooks" element={<WebhooksDoc />} />
                  <Route path="billing" element={<BillingDoc />} />
                  <Route path="security" element={<SecurityDoc />} />
                  <Route path="faq" element={<FaqDoc />} />
                  <Route path="tools-writing" element={<ToolsWritingDoc />} />
                  <Route path="tools-pdf" element={<ToolsPdfDoc />} />
                  <Route path="tools-site" element={<ToolsSiteDoc />} />
                  <Route path="tools-chatbot" element={<ToolsChatbotDoc />} />
                </Route>
              </Route>

              {/* Public trial page */}
              <Route path="/try" element={<TrialPage />} />

              {/* OAuth consent page for the remote MCP connector */}
              <Route path="/oauth/authorize" element={<OAuthConsent />} />

              {/* Auth routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/playground" element={<Playground />} />
              <Route path="/invite/:token" element={<InvitePage />} />

              {/* Checkout callback routes */}
              <Route path="/checkout/success" element={
                <ProtectedRoute>
                  <CheckoutSuccess />
                </ProtectedRoute>
              } />
              <Route path="/checkout/cancel" element={<CheckoutCancel />} />

              {/* Dashboard routes (protected) */}
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <DashboardLayout />
                </ProtectedRoute>
              }>
                <Route index element={<Dashboard />} />
                <Route path="onboarding" element={<OnboardingPage />} />
                <Route path="knowledge-bases" element={<KnowledgeBases />} />
                <Route path="knowledge-bases/:knowledgeBaseId" element={<KnowledgeBaseDetailLayout />} />
                <Route path="knowledge-bases/:knowledgeBaseId/knowledge" element={<KnowledgeBase />} />
                <Route path="followed" element={<Followed />} />
                <Route path="conversations" element={<Conversations />} />
                <Route path="settings" element={<Settings />} />
                <Route path="referrals" element={<Referrals />} />
              </Route>

              {/* Admin operations console (platform staff only) */}
              <Route path="/admin" element={
                <AdminRoute>
                  <AdminLayout />
                </AdminRoute>
              }>
                <Route index element={<AdminOverview />} />
                <Route path="news" element={<AdminNews />} />
                <Route path="users" element={<AdminUsers />} />
                <Route path="knowledge-bases" element={<AdminKnowledgeBases />} />
                <Route path="tokens" element={<AdminTokens />} />
                <Route path="revenue" element={<AdminRevenue />} />
                <Route path="conversations" element={<AdminConversations />} />
                <Route path="storage" element={<AdminStorage />} />
                <Route path="billing" element={<AdminBilling />} />
                <Route path="referrals" element={<AdminReferrals />} />
                <Route path="trials" element={<AdminTrials />} />
                <Route path="funnel" element={<AdminFunnel />} />
                <Route path="kumamoto" element={<AdminKumamoto />} />
                <Route path="kumamoto/:id" element={<AdminKumamotoDetail />} />
                <Route path="tool-failures" element={<AdminToolFailures />} />
                <Route path="llm" element={<AdminLLMProviders />} />
              </Route>

              {/* Public Knowledge-Base landing page (slug-based) */}
              <Route path="/kb/:slug" element={<KbLanding />} />
            </Routes>
          </Suspense>
        </div>
      </Router>
    </ConfigProvider>
  );
};

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;